import type {Component, Editor, RichTextEditorAction} from 'grapesjs';
import {getValidatedUrl} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {getTextLinkColor, setComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {EMAIL_MAKER_COMMANDS, EMAIL_MAKER_STYLES} from '@EmailMakerReact/email-maker-react.constants';
import {getLinkOptionURL} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/rte/rte-selection.util';
import {PMW_LINK_PRESENTATION_STYLE} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components/register-text-types';

/** `editor.RichTextEditor` (the RichTextEditorModule type is not exported). */
type RteManager = Editor['RichTextEditor'];
/** The RTE instance passed to `result`/`update` callbacks (class not exported directly). */
type RteInstance = Parameters<RichTextEditorAction['result']>[0];

/** Value of `data-pmw-type` on button components — they disable the link builder. */
const BUTTON_COMPONENT_TYPE = 'postermywall-button';

const CUSTOM_LINK_INPUT_ID = 'custom-link-input';

/** Returns the custom-link input element, or null if it is not in the DOM. */
function getCustomLinkInput(): HTMLInputElement | null {
  const el = document.getElementById(CUSTOM_LINK_INPUT_ID);
  return el instanceof HTMLInputElement ? el : null;
}

const DEFAULT_LINK_COLOR = '#0000EE';

export function addLinkRteAction(rte: RteManager, editor: Editor): void {
  const linkPlaceholderText = window.i18next.t('pmwjs_link_placeholder');

  rte.add('custom-link', {
    icon:
      '<div class="rte-url-builder-container" data-type="create">' +
      '<div class="icon-container link flex-center radius-4 _full-height"><i class="pmw-icon size-icon-16 icon-link rte-icon spacing-p-2" data-type="create"></i></div>' +
      `<div class="_hidden rte-email-input"><input name="link" placeholder="${linkPlaceholderText}" id="${CUSTOM_LINK_INPUT_ID}" value="" />` +
      '<div class="icon-container flex-center spacing-p-1 _full-height close-input"><i data-type="close" class="pmw-icon icon-close spacing-p-2"></i></div>' +
      '<div class="icon-container flex-center spacing-p-1 _full-height delete-input -danger" data-type="delete"><i data-type="delete" class="pmw-icon icon-delete spacing-p-2"></i></div>' +
      '<div class="icon-container flex-center spacing-p-1 _full-height add-input"><i data-type="add" class="pmw-icon icon-check spacing-p-2"></i></div>' +
      '</div>' +
      '</div>',
    attributes: {
      title: window.i18next.t('pmwjs_link'),
      class: 'gjs-rte-action rte-custom-link',
      'data-type': 'create',
    },
    event: 'change',
    result: (rteInstance: RteInstance) => {
      const inputEl = getCustomLinkInput();
      const val = inputEl?.value;

      if (val) {
        const validatedURL = getValidatedUrl(val);
        rteInstance.exec('createLink', validatedURL);

        const selectedComponent = editor.getSelected();
        if (selectedComponent && !getTextLinkColor(selectedComponent)) {
          setComponentStyle(editor, {
            component: selectedComponent,
            property: EMAIL_MAKER_STYLES.LINK_COLOR,
            value: DEFAULT_LINK_COLOR,
          });
        }

        applyLinkPresentationToLiveAnchors(selectedComponent);

        editor.runCommand(EMAIL_MAKER_COMMANDS.REFRESH_STYLEMANAGER);
      } else {
        const focusEl = rteInstance.selection()?.focusNode?.parentElement;
        if (focusEl) {
          focusEl.removeAttribute('style');
        }

        rteInstance.exec('unlink');
      }

      const customLinkEl = document.querySelector<HTMLElement>('.rte-custom-link');
      customLinkEl?.click();
    },
    update: (rteInstance: RteInstance, action: RichTextEditorAction) => {
      const url = getLinkOptionURL(rteInstance.selection(), editor.getSelected()?.getEl());
      const actionSelectorParent = document.querySelector<HTMLElement>('.rte-url-builder-container');
      const customLinkEl = document.querySelector<HTMLElement>('.rte-custom-link');

      if (!customLinkEl) {
        return 0;
      }

      if (editor.getSelected()?.getEl()?.getAttribute('data-pmw-type') === BUTTON_COMPONENT_TYPE) {
        customLinkEl.classList.add('-disabled');
        return 0;
      }

      const sel = rteInstance.selection();
      if (sel?.anchorOffset === sel?.focusOffset) {
        disableAndCloseCustomLinkBuilder(rte);
        return 0;
      }

      customLinkEl.classList.remove('-disabled');

      const actionBtn = action.btn;

      if (actionSelectorParent) {
        if (url) {
          actionSelectorParent.querySelector<HTMLElement>('.delete-input')?.classList.remove('_hidden');
          actionSelectorParent.querySelector<HTMLElement>('.close-input')?.classList.add('_hidden');
          actionBtn?.classList.add('gjs-rte-active');
        } else {
          actionSelectorParent.querySelector<HTMLElement>('.delete-input')?.classList.add('_hidden');
          actionSelectorParent.querySelector<HTMLElement>('.close-input')?.classList.remove('_hidden');
          actionBtn?.classList.remove('gjs-rte-active');
        }
      }

      const customLinkInput = getCustomLinkInput();
      if (customLinkInput) {
        customLinkInput.value = url;
      }

      return 0;
    },
  });
}

/** Applies PMW link presentation to live anchors immediately so new links do not flash default styling before GrapesJS reparses on blur. */
function applyLinkPresentationToLiveAnchors(textComponent: Component | undefined): void {
  if (!textComponent) {
    return;
  }

  const textEl = textComponent.getEl();
  const color = getTextLinkColor(textComponent) ?? DEFAULT_LINK_COLOR;
  if (!textEl) {
    return;
  }

  textEl.querySelectorAll('a').forEach((anchorEl) => {
    anchorEl.style.color = color;
    anchorEl.style.textDecoration = PMW_LINK_PRESENTATION_STYLE['text-decoration'];
    anchorEl.style.borderRadius = PMW_LINK_PRESENTATION_STYLE['border-radius'];
  });
}

/** Disables and collapses the custom-link builder; called on RTE enable and when the selection collapses. */
export function disableAndCloseCustomLinkBuilder(rte: RteManager): void {
  const customLinkCTA = rte.get('custom-link')?.btn;
  if (!customLinkCTA) {
    return;
  }

  customLinkCTA.classList.add('-disabled');

  if (!customLinkCTA.classList.contains('-open')) {
    return;
  }

  const customLinkBuilderContainer = customLinkCTA.querySelector<HTMLElement>('.rte-url-builder-container');
  const inputEl = customLinkCTA.querySelector<HTMLElement>('.rte-email-input');
  const allActions = rte.getAll();

  customLinkBuilderContainer?.classList.remove('-open');
  customLinkCTA.classList.remove('-open');
  allActions.forEach((action) => {
    action.btn?.classList.remove('_hidden');
  });

  inputEl?.classList.add('_hidden');
}

/**
 * Wires the keyboard and click handlers for the inline URL builder, returning a teardown that
 * removes them so a subsequent remount adds exactly one fresh pair of listeners.
 */
export function initHandlersForCustomURL(): () => void {
  function onKeyUp(e: KeyboardEvent): void {
    if (!(e.target instanceof HTMLElement)) {
      return;
    }
    const target = e.target;
    if (target.id !== CUSTOM_LINK_INPUT_ID) {
      return;
    }

    if (e.key === 'Enter') {
      const container = document.querySelector<HTMLElement>('.rte-url-builder-container');
      container?.dispatchEvent(new Event('change', {bubbles: true}));
    }
  }

  function onClick(e: MouseEvent): void {
    if (!(e.target instanceof HTMLElement)) {
      return;
    }
    const target = e.target;
    const customLinkEl = target.closest<HTMLElement>('.rte-custom-link');

    if (!customLinkEl) {
      return;
    }

    const dataType = target.getAttribute('data-type');
    const linkInput = getCustomLinkInput();
    const elementToBeTriggered = document.querySelector<HTMLElement>('.rte-url-builder-container');

    switch (dataType) {
      case 'create':
      case 'close':
        toggleOpenState(customLinkEl);
        break;

      case 'add':
        elementToBeTriggered?.dispatchEvent(new Event('change', {bubbles: true}));
        break;

      case 'delete':
        if (linkInput) {
          linkInput.value = '';
        }
        elementToBeTriggered?.dispatchEvent(new Event('change', {bubbles: true}));
        break;

      default:
        break;
    }
  }

  document.addEventListener('keyup', onKeyUp);
  document.addEventListener('click', onClick);

  return (): void => {
    document.removeEventListener('keyup', onKeyUp);
    document.removeEventListener('click', onClick);
  };
}

function toggleOpenState(customLinkEl: HTMLElement): void {
  const inputEl = customLinkEl.querySelector<HTMLElement>('.rte-email-input');
  if (!inputEl) {
    return;
  }

  customLinkEl.classList.toggle('-open');
  inputEl.classList.toggle('_hidden');

  const parentContainer = inputEl.closest<HTMLElement>('.rte-url-builder-container');
  parentContainer?.classList.toggle('-open');

  const actionContainer = parentContainer?.closest<HTMLElement>('.gjs-rte-action');
  actionContainer?.parentElement?.querySelectorAll<HTMLElement>('.gjs-rte-action, .rte-action-divider').forEach((el) => {
    if (el !== actionContainer) {
      el.classList.toggle('_hidden');
    }
  });

  if (!inputEl.classList.contains('_hidden')) {
    const urlInput = inputEl.querySelector<HTMLInputElement>(`#${CUSTOM_LINK_INPUT_ID}`);
    urlInput?.focus();
  }
}
